import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/Components/ui/button';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApiUrl } from '@/config/api';

function ImageUpload({ onUpload, companyLogo, onBannerUpload, companyBanner }) {
  const [profileImage, setProfileImage] = useState(null);
  const [bannerImage, setBannerImage]   = useState(null);
  const profileInputRef = useRef(null);
  const bannerInputRef  = useRef(null);

  useEffect(() => {
    if (companyLogo && !profileImage)
      setProfileImage(getApiUrl(`/api/hirewithus/company/logo/${companyLogo}`));
  }, [companyLogo]);

  useEffect(() => {
    if (companyBanner && !bannerImage)
      setBannerImage(getApiUrl(`/api/hirewithus/company/banner/${companyBanner}`));
  }, [companyBanner]);

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileImage(URL.createObjectURL(file));
    onUpload?.(file);
  };

  const handleBannerImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerImage(URL.createObjectURL(file));
    onBannerUpload?.(file);
  };

  // Only accept the same formats the server does; the parent re-validates
  // size and shows the friendly error, so we just gate obviously-wrong drops.
  const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !ACCEPTED.includes(file.type)) return;
    if (type === 'profile') { setProfileImage(URL.createObjectURL(file)); onUpload?.(file); }
    else                    { setBannerImage(URL.createObjectURL(file));  onBannerUpload?.(file); }
  };

  const handleRemove = (e, type) => {
    e.stopPropagation();
    if (type === 'profile') { setProfileImage(null); if (profileInputRef.current) profileInputRef.current.value = ''; }
    else                    { setBannerImage(null);  if (bannerInputRef.current)  bannerInputRef.current.value  = ''; }
  };

  return (
    <div className="space-y-3">

      {/* ── Logo ── */}
      <div className="flex items-center gap-4">
        {/* Fixed-size logo preview / placeholder */}
        <div
          className={cn(
            "relative group flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed overflow-hidden transition-all cursor-pointer",
            profileImage
              ? "border-border hover:border-primary/50"
              : "border-muted-foreground/30 hover:border-primary bg-muted/30"
          )}
          onClick={() => profileInputRef.current?.click()}
          onDrop={(e) => handleDrop(e, 'profile')}
          onDragOver={(e) => e.preventDefault()}
        >
          <input ref={profileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleProfileImageChange} className="hidden" />

          {profileImage ? (
            <>
              <img src={profileImage} alt="Company logo"
                onError={() => setProfileImage(null)}
                className="w-full h-full object-contain p-1" />
              {/* hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <Upload className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1">
              <ImageIcon className="w-7 h-7 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">Logo</span>
            </div>
          )}
        </div>

        {/* Text + actions */}
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-foreground">Company Logo</p>
          <p className="text-xs text-muted-foreground">Square image, 400×400px recommended. PNG or JPG, max 5 MB.</p>
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={() => profileInputRef.current?.click()} className="h-7 text-xs px-3">
              <Upload className="w-3 h-3 mr-1.5" />
              {profileImage ? 'Change' : 'Upload'}
            </Button>
            {profileImage && (
              <Button type="button" size="sm" variant="ghost"
                onClick={(e) => handleRemove(e, 'profile')}
                className="h-7 text-xs px-2 text-destructive hover:text-destructive">
                <X className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Banner ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Banner Image</p>
            <p className="text-xs text-muted-foreground">Wide banner, 1520×400px recommended. PNG or JPG, max 5 MB.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline"
              onClick={() => bannerInputRef.current?.click()} className="h-7 text-xs px-3">
              <Upload className="w-3 h-3 mr-1.5" />
              {bannerImage ? 'Change' : 'Upload'}
            </Button>
            {bannerImage && (
              <Button type="button" size="sm" variant="ghost"
                onClick={(e) => handleRemove(e, 'banner')}
                className="h-7 text-xs px-2 text-destructive hover:text-destructive">
                <X className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </div>

        <input ref={bannerInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={handleBannerImageChange} className="hidden" />

        {/* Banner preview — fixed height, never grows */}
        <div
          className={cn(
            "relative group w-full h-16 rounded-lg border-2 border-dashed overflow-hidden transition-all cursor-pointer",
            bannerImage
              ? "border-border hover:border-primary/50"
              : "border-muted-foreground/30 hover:border-primary bg-muted/30"
          )}
          onClick={() => bannerInputRef.current?.click()}
          onDrop={(e) => handleDrop(e, 'banner')}
          onDragOver={(e) => e.preventDefault()}
        >
          {bannerImage ? (
            <>
              <img src={bannerImage} alt="Banner"
                onError={() => setBannerImage(null)}
                className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <Upload className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
              <ImageIcon className="w-7 h-7 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                <span className="text-primary font-medium">Click to upload</span> or drag and drop
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default ImageUpload;
